import { useEffect, useState, useRef } from "react";
import { api, apiError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { FolderOpen, Download, Search, Upload, Trash2, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const BACKEND = BACKEND_URL;
const ROLE_OPTS = [["internal_staff", "Internal staff"], ["partner", "Partner"]];

export default function Resources() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "trainer"].includes(user.role);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Guides", roles: ["internal_staff", "partner"] });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = () => {
    api.get("/resources", { params: { search: search || undefined, category: category === "all" ? undefined : category } })
      .then(({ data }) => setData(data));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [search, category]);

  const dl = (id) => window.open(`${BACKEND}/api/resources/${id}/download`, "_blank");

  const upload = async () => {
    if (!form.title || !file) return toast.error("Add a title and choose a file");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("category", form.category);
      fd.append("roles", form.roles.join(","));
      fd.append("file", file);
      await api.post("/admin/resources", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resource added");
      setUploadOpen(false);
      setForm({ title: "", description: "", category: "Guides", roles: ["internal_staff", "partner"] });
      setFile(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/admin/resources/${id}`);
    toast.success("Resource deleted");
    load();
  };

  const toggleRole = (r) => setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));

  return (
    <div className="fade-in">
      <PageHeader title="Resource library" subtitle="Guides, templates and supplier documents.">
        {isAdmin && (
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button className="btn-brand" data-testid="add-resource-button"><Upload className="mr-2 h-4 w-4" /> Add resource</Button>
            </DialogTrigger>
            <DialogContent data-testid="upload-resource-modal">
              <DialogHeader><DialogTitle>Add a resource</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="resource-title-input" className="h-10 w-full rounded-lg border border-input bg-card px-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
                <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2} className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
                <div className="flex gap-3">
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="flex-1" data-testid="resource-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(data?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">Visible to:</span>
                  {ROLE_OPTS.map(([r, label]) => (
                    <label key={r} className="flex items-center gap-1.5">
                      <input type="checkbox" checked={form.roles.includes(r)} onChange={() => toggleRole(r)} data-testid={`role-vis-${r}`} /> {label}
                    </label>
                  ))}
                </div>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-3 text-sm">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 truncate">{file ? file.name : "Choose a file (max 25 MB)"}</span>
                  <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} data-testid="resource-file-input" />
                  <span className="rounded bg-secondary px-2 py-1 text-xs">Browse</span>
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
                <Button className="btn-brand" onClick={upload} disabled={saving} data-testid="save-resource-button">{saving ? "Uploading…" : "Add resource"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="mb-6 flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="resource-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search resources…"
            className="h-10 w-full rounded-lg border border-input bg-card pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[180px]" data-testid="resource-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {(data?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!data ? null : data.resources.length === 0 ? (
        <EmptyState icon={FolderOpen} title="No resources found" description="Try a different search or category." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="resources-grid">
          {data.resources.map((r) => (
            <div key={r.id} className="card-surface flex flex-col p-5" data-testid={`resource-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-[#33B6E4]/12 text-[#33B6E4]"><FileText className="h-5 w-5" /></div>
                <span className="rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground">{r.category}</span>
              </div>
              <h3 className="mt-3 font-heading text-[16px] font-semibold leading-snug">{r.title}</h3>
              <p className="mt-1 line-clamp-2 flex-1 text-sm text-muted-foreground">{r.description}</p>
              <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                <span>{r.file_size}</span>
                <span className="tnum">{r.download_count} downloads</span>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" className="btn-brand flex-1" onClick={() => dl(r.id)} data-testid={`download-resource-${r.id}`}>
                  <Download className="mr-1.5 h-3.5 w-3.5" /> Download
                </Button>
                {isAdmin && (
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`delete-resource-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
